// Account picker dropdown for Bank account history.
// Trigger: box with account name + chevron in its own section on the right.
// Panel: ALL accounts with type on the right in italic grey; sub-accounts indent.

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { AccountPickerItem } from "../types";

interface AccountPickerDropdownProps {
  accounts: AccountPickerItem[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}

export default function AccountPickerDropdown({ accounts, selectedId, onSelect }: AccountPickerDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const selected = accounts.find((a) => a.id === selectedId);

  const handlePick = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" style={{ minWidth: 280 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex items-center justify-between bg-white border border-line rounded-lg px-3 py-2 cursor-pointer text-[13px] font-semibold text-ink hover:bg-[#FAFBFB]"
      >
        <span>{selected?.name ?? "Select account"}</span>
        <span className="inline-flex items-center justify-center w-5 h-5 border-l border-line ml-2.5 pl-2.5 text-ink3">
          <ChevronDown size={12} strokeWidth={2.5} />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-line rounded-xl shadow-[0_10px_32px_rgba(14,26,26,0.14)] py-1 z-50 max-h-[360px] overflow-y-auto" style={{ width: 520 }}>
          {accounts.map((a) => {
            const isSel = a.id === selectedId;
            const indent = a.parentId ? "pl-8" : "pl-3.5";
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => handlePick(a.id)}
                className={`w-full flex justify-between items-center ${indent} pr-3.5 py-2.5 text-left text-[13px] bg-transparent border-none cursor-pointer hover:bg-panel ${isSel ? "bg-panel" : ""}`}
              >
                <span className="text-ink font-semibold">{a.name}</span>
                <span className="text-ink3 italic text-[12.5px] ml-4">{a.type}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}